import React, { useEffect, useState } from 'react';
import { Database, Eye, EyeOff, Loader2, Save, X } from 'lucide-react';
import ixcSourceService from '../services/ixcSourceService';

const emptyForm = {
  connection_type: 'database',
  mcp_url: '',
  mcp_token: '',
  company_id: '',
  host: '',
  port: 3306,
  database: '',
  username: '',
  password: '',
  is_active: true,
};

const IxcSourceModal = ({ open, source, companies, onClose, onSaved }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (!open) return;

    setForm(source ? {
      connection_type: source.connection_type || 'database',
      mcp_url: source.mcp_url || '',
      mcp_token: '',
      company_id: source.company_id,
      host: source.host,
      port: source.port,
      database: source.database,
      username: source.username,
      password: '',
      is_active: source.is_active,
    } : emptyForm);
    setErrors({});
    setShowPassword(false);
  }, [open, source]);

  if (!open) return null;

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setErrors({});

    try {
      const payload = { ...form, port: Number(form.port) };
      if (payload.connection_type === 'mcp') {
        ['host', 'port', 'database', 'username', 'password'].forEach((key) => delete payload[key]);
        if (!payload.mcp_token) delete payload.mcp_token;
      } else {
        delete payload.mcp_url;
        delete payload.mcp_token;
      }
      if (source && !payload.password) delete payload.password;

      if (source) {
        delete payload.company_id;
        await ixcSourceService.update(source.id, payload);
      } else {
        await ixcSourceService.create(payload);
      }
      onSaved(source ? 'Integração IXC atualizada.' : 'Integração IXC criada.');
    } catch (error) {
      if (error.response?.status === 422) {
        setErrors(error.response?.data?.errors || {});
      } else {
        setErrors({ general: [error.response?.data?.message || 'Não foi possível salvar a integração.'] });
      }
    } finally {
      setSaving(false);
    }
  };

  const fieldClass = 'w-full rounded-xl border bg-[color:var(--surface-muted)] px-3 py-2.5 text-sm outline-none transition placeholder:text-[color:var(--text-muted)] hover:border-[color:var(--text-muted)] focus:border-[color:var(--accent)] focus:ring-2 disabled:cursor-not-allowed disabled:opacity-70';
  const fieldStyle = {
    borderColor: 'color-mix(in srgb, var(--text-muted) 42%, transparent)',
    color: 'var(--text-primary)',
    '--tw-ring-color': 'color-mix(in srgb, var(--accent) 30%, transparent)',
  };
  const optionStyle = { backgroundColor: 'var(--surface-strong)', color: 'var(--text-primary)' };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-labelledby="ixc-source-modal-title">
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl border shadow-2xl theme-surface" style={{ borderColor: 'var(--border)' }}>
        <div className="flex items-start justify-between border-b px-6 py-5" style={{ borderColor: 'var(--border)' }}>
          <div className="flex gap-3">
            <div className="rounded-xl bg-emerald-500/10 p-2.5 text-emerald-500"><Database className="h-5 w-5" /></div>
            <div>
              <h2 id="ixc-source-modal-title" className="text-xl font-semibold theme-text">{source ? 'Editar integração IXC' : 'Nova integração IXC'}</h2>
              <p className="mt-1 text-sm text-[color:var(--text-muted)]">Senhas e tokens são cifrados e nunca retornam pela API.</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg p-2 text-[color:var(--text-muted)] transition hover:bg-[color:var(--surface-muted)] hover:text-[color:var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]" aria-label="Fechar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-5 p-6">
          {errors.general && <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-500">{errors.general[0]}</div>}

          <div>
            <label htmlFor="company_id" className="mb-1.5 block text-sm font-medium theme-text">Empresa</label>
            <select id="company_id" disabled={Boolean(source)} required value={form.company_id} onChange={(e) => update('company_id', e.target.value)} className={fieldClass} style={fieldStyle} aria-invalid={Boolean(errors.company_id)} aria-describedby={errors.company_id ? 'company_id-error' : undefined}>
              <option value="" style={optionStyle}>Selecione uma empresa</option>
              {companies.map((company) => <option key={company.id} value={company.id} style={optionStyle}>{company.name}</option>)}
            </select>
            {errors.company_id && <p id="company_id-error" className="mt-1 text-xs text-rose-500">{errors.company_id[0]}</p>}
          </div>

          <div>
            <label htmlFor="connection_type" className="mb-1.5 block text-sm font-medium theme-text">Tipo de conexão</label>
            <select id="connection_type" value={form.connection_type} onChange={(e) => { update('connection_type', e.target.value); setErrors({}); }} className={fieldClass} style={fieldStyle}>
              <option value="database" style={optionStyle}>Banco de dados direto</option>
              <option value="mcp" style={optionStyle}>Via MCP</option>
            </select>
            {errors.connection_type && <p role="alert" className="mt-1 text-xs text-rose-500">{errors.connection_type[0]}</p>}
          </div>

          {form.connection_type === 'mcp' ? (
            <>
              <p className="text-sm theme-text-muted">As credenciais do banco IXC permanecem no serviço MCP. Use o endpoint da empresa selecionada.</p>
              <div>
                <label htmlFor="mcp_url" className="mb-1.5 block text-sm font-medium theme-text">URL do MCP</label>
                <input id="mcp_url" required type="url" value={form.mcp_url} onChange={(e) => update('mcp_url', e.target.value)} className={fieldClass} style={fieldStyle} placeholder="https://n8n.exemplo.com/mcp/ixc" aria-invalid={Boolean(errors.mcp_url)} />
                {errors.mcp_url && <p role="alert" className="mt-1 text-xs text-rose-500">{errors.mcp_url[0]}</p>}
              </div>
              <div>
                <label htmlFor="mcp_token" className="mb-1.5 block text-sm font-medium theme-text">Token de acesso {source?.mcp_token_configured && <span className="font-normal theme-text-muted">(deixe vazio para manter no mesmo endpoint)</span>}</label>
                <input id="mcp_token" required={!source?.mcp_token_configured || form.mcp_url !== source?.mcp_url} type="password" value={form.mcp_token} onChange={(e) => update('mcp_token', e.target.value)} className={fieldClass} style={fieldStyle} autoComplete="new-password" aria-invalid={Boolean(errors.mcp_token)} />
                {errors.mcp_token && <p role="alert" className="mt-1 text-xs text-rose-500">{errors.mcp_token[0]}</p>}
              </div>
            </>
          ) : (
            <>
          <div className="grid gap-4 sm:grid-cols-[1fr_130px]">
            <div>
              <label htmlFor="host" className="mb-1.5 block text-sm font-medium theme-text">Host</label>
              <input id="host" required value={form.host} onChange={(e) => update('host', e.target.value)} className={fieldClass} style={fieldStyle} placeholder="mysql.exemplo.interno" aria-invalid={Boolean(errors.host)} aria-describedby={errors.host ? 'host-error' : undefined} />
              {errors.host && <p id="host-error" className="mt-1 text-xs text-rose-500">{errors.host[0]}</p>}
            </div>
            <div>
              <label htmlFor="port" className="mb-1.5 block text-sm font-medium theme-text">Porta</label>
              <input id="port" required type="number" min="1" max="65535" value={form.port} onChange={(e) => update('port', e.target.value)} className={fieldClass} style={fieldStyle} aria-invalid={Boolean(errors.port)} aria-describedby={errors.port ? 'port-error' : undefined} />
              {errors.port && <p id="port-error" className="mt-1 text-xs text-rose-500">{errors.port[0]}</p>}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="database" className="mb-1.5 block text-sm font-medium theme-text">Banco de dados</label>
              <input id="database" required value={form.database} onChange={(e) => update('database', e.target.value)} className={fieldClass} style={fieldStyle} aria-invalid={Boolean(errors.database)} aria-describedby={errors.database ? 'database-error' : undefined} />
              {errors.database && <p id="database-error" className="mt-1 text-xs text-rose-500">{errors.database[0]}</p>}
            </div>
            <div>
              <label htmlFor="username" className="mb-1.5 block text-sm font-medium theme-text">Usuário somente leitura</label>
              <input id="username" required value={form.username} onChange={(e) => update('username', e.target.value)} className={fieldClass} style={fieldStyle} autoComplete="off" aria-invalid={Boolean(errors.username)} aria-describedby={errors.username ? 'username-error' : undefined} />
              {errors.username && <p id="username-error" className="mt-1 text-xs text-rose-500">{errors.username[0]}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="password" className="mb-1.5 block text-sm font-medium theme-text">Senha {source && <span className="font-normal theme-text-muted">(deixe vazia para manter)</span>}</label>
            <div className="relative">
              <input id="password" required={!source} type={showPassword ? 'text' : 'password'} value={form.password} onChange={(e) => update('password', e.target.value)} className={`${fieldClass} pr-11`} style={fieldStyle} autoComplete="new-password" aria-invalid={Boolean(errors.password)} aria-describedby={errors.password ? 'password-error' : undefined} />
              <button type="button" onClick={() => setShowPassword((current) => !current)} className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-2 text-[color:var(--text-muted)] transition hover:bg-[color:var(--surface-strong)] hover:text-[color:var(--text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]" aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}>
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {errors.password && <p id="password-error" className="mt-1 text-xs text-rose-500">{errors.password[0]}</p>}
          </div>

            </>
          )}

          <label className="flex items-center gap-3 rounded-xl border bg-[color:var(--surface-muted)] p-3" style={{ borderColor: 'color-mix(in srgb, var(--text-muted) 42%, transparent)' }}>
            <input type="checkbox" checked={form.is_active} onChange={(e) => update('is_active', e.target.checked)} className="h-4 w-4 accent-emerald-500" />
            <span><span className="block text-sm font-medium theme-text">Integração ativa</span><span className="text-xs theme-text-muted">Fontes inativas não atendem consultas.</span></span>
          </label>

          <div className="flex justify-end gap-3 border-t pt-5" style={{ borderColor: 'var(--border)' }}>
            <button type="button" onClick={onClose} className="rounded-xl border px-4 py-2.5 text-sm font-medium theme-text transition hover:bg-[color:var(--surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--accent)]" style={{ borderColor: 'color-mix(in srgb, var(--text-muted) 42%, transparent)' }}>Cancelar</button>
            <button disabled={saving} className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-emerald-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--surface)] disabled:cursor-wait disabled:opacity-60">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Salvar integração
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default IxcSourceModal;
